import React, { ReactNode, useEffect, useState } from 'react'
import axios from 'axios'
import Modal from '../modal'
import { t } from '../../../i18n'
import RatingSummary from '../comments/RatingSummary'

interface CopySourceBase {
  id: number
  name: string
  version_number: number
  average_rating: number | null
  comments_count: number
}

interface CopyFromModalProps<Source extends CopySourceBase> {
  url: string
  params: Record<string, string | number | null>
  // i18n scope holding title, search_placeholder, loading, load_error and no_sources
  scope: string
  renderMeta?: (source: Source) => ReactNode
  onSelect: (source: Source) => void
  onClose: () => void
}

const CopyFromModal = <Source extends CopySourceBase>({
  url,
  params,
  scope,
  renderMeta,
  onSelect,
  onClose
}: CopyFromModalProps<Source>) => {
  const [sources, setSources] = useState<Source[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  // The picker is mounted when opened, so its sources load once per opening
  useEffect(() => {
    axios
      .get<Source[]>(url, { params })
      .then((resp) => setSources(resp.data))
      .catch(() => setLoadError(true))
  }, [])

  const query = searchQuery.trim().toLowerCase()
  const visibleSources = (sources ?? []).filter((source) =>
    source.name.toLowerCase().includes(query)
  )

  return (
    <Modal headerText={t('title', { scope })} widen onClose={onClose}>
      <div className="form-group mb-3">
        <input
          className="form-control"
          placeholder={t('search_placeholder', { scope })}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {loadError && <p className="text-danger">{t('load_error', { scope })}</p>}
      {!loadError && sources === null && (
        <p className="text-secondary">{t('loading', { scope })}</p>
      )}
      {sources !== null && visibleSources.length === 0 && (
        <p className="text-secondary">{t('no_sources', { scope })}</p>
      )}

      {visibleSources.length > 0 && (
        <div className="copy-from-modal__list">
          {visibleSources.map((source) => (
            <button
              key={source.id}
              type="button"
              className="btn btn-light w-100 text-start mb-2 d-flex align-items-center justify-content-between"
              onClick={() => onSelect(source)}
            >
              <span>
                {source.name}{' '}
                <span className="text-secondary">v{source.version_number}</span>
              </span>
              <span className="text-secondary">
                {renderMeta?.(source)}
                {renderMeta && source.comments_count > 0 && ' · '}
                <RatingSummary
                  averageRating={source.average_rating}
                  commentsCount={source.comments_count}
                />
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="buttons mt-3 d-flex justify-content-end">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          {t('cancel')}
        </button>
      </div>
    </Modal>
  )
}

export default CopyFromModal
